/**
 * ReasonCooccurrenceMatrix — symmetric heatmap of how often pairs of
 * scoring-reason families co-occur in the top 200 highest-scoring
 * events. Diagonal = single-family frequency. Off-diagonal = pair
 * frequency. Quick visual answer to "which signals fire together?".
 *
 * Rendered as a CSS grid (no recharts dependency for matrix).
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useFilters } from "@/hooks/useFilters";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { Card, CardHeader, EmptyState, ErrorState, Skeleton } from "@/components/primitives";
import { fmtNumber } from "@/lib/format";

export function ReasonCooccurrenceMatrix() {
  const { filters } = useFilters();
  const { tick } = useAutoRefresh();
  const q = useQuery({
    queryKey: ["reason-cooccurrence", filters, tick],
    queryFn: ({ signal }) => api.reasonCooccurrence(filters, signal),
  });

  const data = q.data?.data;

  return (
    <Card>
      <CardHeader
        title="Reason co-occurrence"
        subtitle="Top 200 highest-scoring events: how often each pair of scoring-reason families fires together. Diagonal = single-family frequency."
      />
      {q.isLoading ? (
        <Skeleton className="h-64" />
      ) : q.error ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : !data || data.families.length === 0 ? (
        <EmptyState title="No high-risk events with reasons in this window." />
      ) : (
        <Heatmap families={data.families} matrix={data.matrix} sampleSize={data.sampleSize} />
      )}
    </Card>
  );
}

function Heatmap({ families, matrix, sampleSize }: { families: string[]; matrix: number[][]; sampleSize: number }) {
  const max = Math.max(1, ...matrix.flat());
  const cell = "min(40px, 5vw)";
  return (
    <div className="space-y-2 overflow-x-auto">
      <div
        className="inline-grid gap-px"
        style={{ gridTemplateColumns: `100px repeat(${families.length}, ${cell})` }}
      >
        <div />
        {families.map((f) => (
          <div key={"col-" + f} className="origin-bottom-left -rotate-45 text-[11px] text-fg-subtle" style={{ height: cell }} title={f}>
            {f}
          </div>
        ))}
        {families.map((row, i) => (
          <Row key={"row-" + row} row={row} i={i} families={families} matrix={matrix} max={max} cell={cell} />
        ))}
      </div>
      <p className="text-[11px] text-fg-subtle">
        Sample of {fmtNumber(sampleSize)} top high-risk events. Darker = more frequent. Hover any cell for the exact count.
      </p>
    </div>
  );
}

function Row({ row, i, families, matrix, max, cell }: {
  row: string;
  i: number;
  families: string[];
  matrix: number[][];
  max: number;
  cell: string;
}) {
  return (
    <>
      <div className="truncate pr-2 text-right text-[11px] text-fg" title={row}>{row}</div>
      {families.map((col, j) => {
        const v = matrix[i]?.[j] ?? 0;
        const t = v / max;
        return (
          <div
            key={col + "-" + row}
            title={`${row} \u00d7 ${col}: ${fmtNumber(v)} events`}
            style={{
              width: cell, height: cell,
              backgroundColor: `rgb(var(--accent) / ${0.05 + t * 0.85})`,
            }}
            className="grid place-items-center text-[10px] text-fg"
          >
            {v > 0 ? v : ""}
          </div>
        );
      })}
    </>
  );
}
